// Rules for loot patterns: ways of pairing kinds of creatures with kinds of items,
// repeated for each damage type, like "creatures that deal Fire damage sometimes drop
// weapons with a magic item keyword that adds Fire damage".
//
// Each pattern has CR bands, and each band gets its own weight and rarity range,
// so stronger creatures can drop rarer things more often.
// A pattern becomes one loot entry per damage type per band that has both creatures and items.
//
// What creatures deal and resist, and what items add and protect against, is worked out
// ahead of time (see loot_analysis), so everything here works on plain data and can be tested.

export const MAGICAL_DAMAGE_TYPES = ["Acid","Cold","Fire","Force","Lightning","Necrotic","Poison","Psychic","Radiant","Thunder"];

export type CreatureRelation = "Deals" | "Resists";
export type ItemRelation = "Adds" | "Protects Against";

export interface PatternBandLike {
  min_cr: number;
  max_cr: number;
  weight: number;
  min_rarity: number; // 0 Common ... 5 Artifact
  max_rarity: number;
  enabled: boolean;
}

export interface PatternLike {
  true_id: string;
  name: string;
  damage_types: string[];
  creature_relation: CreatureRelation;
  item_relation: ItemRelation;
  item_types: string[]; // Empty means any
  bands: PatternBandLike[];
}

export interface CreatureFacts {
  id: string;
  name: string;
  creature_type: string;
  cr: number;
  min_cr: number; // Scalable creatures cover a range
  max_cr: number;
  dealt: string[];
  resisted: string[];
  fits_extra: boolean; // Whether it fits the pattern's other creature rules (type, size, etc.)
}

export interface ItemFacts {
  id: string; // PseudoItem id
  name: string;
  item_type: string;
  rarity: number;
  adds: string[]; // Damage types it adds or deals
  resists: string[]; // Damage types it grants resistance or immunity to
}

export const DEFAULT_BANDS: PatternBandLike[] = [
  { min_cr: 0, max_cr: 4, weight: 1, min_rarity: 1, max_rarity: 1, enabled: true },
  { min_cr: 5, max_cr: 10, weight: 1, min_rarity: 1, max_rarity: 2, enabled: true },
  { min_cr: 11, max_cr: 16, weight: 1, min_rarity: 2, max_rarity: 3, enabled: true },
  { min_cr: 17, max_cr: 30, weight: 1, min_rarity: 3, max_rarity: 4, enabled: true },
];

export const creatureFits = (creature: CreatureFacts, pattern: PatternLike, damage_type: string) => {
  const list = pattern.creature_relation === "Deals" ? creature.dealt : creature.resisted;
  return creature.fits_extra && list.includes(damage_type);
};

// Scalable creatures are in a band if any of their CRs are.
// Creatures that don't scale have 0 for both min_cr and max_cr.
export const creatureInBand = (creature: CreatureFacts, band: PatternBandLike) => {
  const scalable = creature.max_cr > creature.min_cr && creature.max_cr > 0;
  const min = scalable ? creature.min_cr : creature.cr;
  const max = scalable ? creature.max_cr : creature.cr;
  return min <= band.max_cr && max >= band.min_cr;
};

export const itemFits = (item: ItemFacts, pattern: PatternLike, damage_type: string) => {
  const list = pattern.item_relation === "Adds" ? item.adds : item.resists;
  return list.includes(damage_type) &&
    (pattern.item_types.length === 0 || pattern.item_types.includes(item.item_type));
};

export const itemInBand = (item: ItemFacts, band: PatternBandLike) =>
  item.rarity >= band.min_rarity && item.rarity <= band.max_rarity;

export interface BandPreview {
  band: PatternBandLike;
  creatures: CreatureFacts[];
  items: ItemFacts[];
}

export interface DamageTypePreview {
  damage_type: string;
  creatures: CreatureFacts[]; // Every creature that fits, in any band
  items: ItemFacts[]; // Every item that fits, at any rarity
  bands: BandPreview[];
}

export const previewPattern = (pattern: PatternLike, creatures: CreatureFacts[], items: ItemFacts[]): DamageTypePreview[] => {
  return pattern.damage_types.map(damage_type => {
    const fitting_creatures = creatures.filter(o => creatureFits(o, pattern, damage_type));
    const fitting_items = items.filter(o => itemFits(o, pattern, damage_type));
    return {
      damage_type,
      creatures: fitting_creatures,
      items: fitting_items,
      bands: pattern.bands.map(band => ({
        band,
        creatures: fitting_creatures.filter(o => creatureInBand(o, band)),
        items: fitting_items.filter(o => itemInBand(o, band)),
      })),
    };
  });
};

export interface PlannedEntry {
  name: string;
  damage_type: string;
  band: PatternBandLike;
  weight: number;
  item_ids: string[];
  creature_count: number;
}

const crLabel = (cr: number) => {
  switch (cr) {
    case 0.125: return "1/8";
    case 0.25: return "1/4";
    case 0.5: return "1/2";
    default: return `${cr}`;
  }
};

export const bandLabel = (band: PatternBandLike) =>
  band.min_cr === band.max_cr ? `CR ${crLabel(band.min_cr)}` : `CR ${crLabel(band.min_cr)}-${crLabel(band.max_cr)}`;

// The entries a pattern makes: one per damage type per enabled band with weight, creatures, and items.
export const planEntries = (pattern: PatternLike, creatures: CreatureFacts[], items: ItemFacts[]): PlannedEntry[] => {
  const planned: PlannedEntry[] = [];
  for (const preview of previewPattern(pattern, creatures, items)) {
    for (const band_preview of preview.bands) {
      const band = band_preview.band;
      if (!band.enabled || band.weight <= 0 || band_preview.creatures.length === 0 || band_preview.items.length === 0) {
        continue;
      }
      planned.push({
        name: `${pattern.name}: ${preview.damage_type} (${bandLabel(band)})`,
        damage_type: preview.damage_type,
        band,
        weight: band.weight,
        item_ids: band_preview.items.map(o => o.id),
        creature_count: band_preview.creatures.length,
      });
    }
  }
  return planned;
};
